<template>
  <b-modal id="tooth-status-modal"
           :title="$t('condition')"
           ref="toothModal"
           @hide="onHide"
           @show="loadReportUses"
           hide-footer
           size="xl"
           centered>
    <div>
      <CheckboxGroup v-if="innerTooth"
                     :label="$t('apply_to_whole_tooth')"
                     fid="whole-tooth"
                     v-model="whole"/>
    </div>
    <div class="border-top py-3 border-bottom">
      <div class="row row-narrow">
        <template v-for="(s, sind) in statuses">
          <div class="col-md-6 col-6"
               v-if="!s.deleted_at"
               :key="`tooth-status-${sind}`">
            <div :class="{ active: toothStatusesOfSelectedAreas.includes(s.id) }"
                 class="pointer status-item">
              <div>
                <div class="row mb-1 row-narrow">
                  <div class="col col-auto" @click="statusSelected(s)">
                    <div class="tooth-status-color"
                         :class="{ bordered: ['transparent','rgb(255, 255, 255)','#ffffff','#FFFFFF'].includes(s.color) }"
                         :style="`background-color: ${s.color}`"></div>
                  </div>
                  <div class="col">
                    <div class="title d-inline-block align-middle"
                         @click="statusSelected(s)">
                      {{ s.title }}
                      <span v-if="statusesInReports.includes(s.id) || s.standardf43" class="ml-2">(043)</span>
                    </div>
                    <a v-if="canEdit"
                       class="ml-3 d-inline-block align-middle edit-svg-btn"
                       :class="{ standard: !s.clinic_id }"
                       @click="editStatus(s)">
                      <EditSvg/>
                    </a>
                    <a v-if="s.clinic_id > 0 && canEdit"
                       class="ml-3 d-inline-block align-middle"
                       @click="deleteStatus(s, sind)">
                      <TrashSvg class="svg-light"/>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div>
      <div class="pt-4">
        <div>
          <a class="btn-themed btn-themed-squared btn-themed-outline d-inline-block"
             v-if="!editingStatus && canEdit"
             @click="addNew">
            {{ $t('add') }}
          </a>
        </div>

        <div v-if="editingStatus">
          <div class="row align-items-end">
            <div class="col-md-4 col-xl-3 col-12" v-if="editingStatus.clinic_id">
              <InputGroup v-model="editingStatus.title"
                          :show-errors="true"
                          validation-id="title"
                          :label="$t('title')"/>
            </div>
            <div class="col-md-4 col-xl-3 col-12">
              <InputGroup v-model="editingStatus.short_name"
                          :show-errors="true"
                          :readonly="!editingStatus.clinic_id"
                          validation-id="short_name"
                          :label="$t('short_name')"/>
            </div>
            <div class="col-md-4 col-xl-3 col-12" v-if="editingStatus.clinic_id">
              <InputGroup v-model="editingStatus.color"
                          :show-errors="true"
                          validation-id="color"
                          type="color"
                          :label="$t('color')"/>
            </div>
            <div class="col-md-4 col-xl-3 col-12">
              <CheckboxGroup v-model="editingStatus.useInForm"
                             :key="`counter-${counter}`"
                             fid="useInForm"
                             :readonly="!!editingStatus.standardf43"
                             :checkboxvalue="1"
                             :show-errors="true"
                             :label="$t('include_in_f043f058')"/>
            </div>
          </div>
          <div class="row mt-4 row-narrow justify-content-end">
            <div class="col col-auto">
              <button @click="cancel"
                      class="btn-themed btn-themed-squared w-100 text-center d-inline-block btn-themed-outline">
                {{ $t('cancel') }}
              </button>
            </div>
            <div class="col col-auto">
              <button @click="saveStatus"
                      :disabled="loading"
                      class="btn-themed btn-themed-squared w-100 text-center d-inline-block">
                {{ $t('save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script setup>

import { ClinicService } from "@/services/api/clinic.api.service"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { PatientService } from "@/services/api/patient.api.service"
import { useStore } from "vuex"
import CheckboxGroup from "@/components/form/CheckboxGroup"
import InputGroup from "@/components/form/InputGroup"
import EditSvg from '@/assets/svg-vue/general/edit.svg'
import TrashSvg from '@/assets/svg-vue/general/trash_tr.svg'
import {appendFormdata, copyObject} from "@/extensions/prototypes/prototypes"
import {ref, computed, watch, onBeforeMount, getCurrentInstance} from "vue"

const store = useStore()

let bvModal = null
let noty = null
let i18n = null

const whole = ref(false)
const editingStatus = ref(null)
const loading = ref(false)
const statusesInReports = ref([])
const counter = ref(1)

const patient = computed(() => store.state.patient.patient)
const rights = computed(() => store.state.auth.rights)
const clinic = computed(() => store.state.auth.clinic)
const selectedTooth = computed(() => store.state.teeth.selectedTooth)
const teeth = computed(() => store.state.teeth.teeth)
const selectedToothArea = computed(() => store.state.teeth.selectedToothArea)
const selectedToothAreas = computed(() => store.state.teeth.selectedToothAreas)
const toothStatuses = computed(() => store.state.teeth.toothStatuses)

const statuses = computed(() => {
  let st = []
  if(!selectedToothArea.value) return []
  if(['gum','tooth'].includes(selectedToothArea.value)) {
    st = toothStatuses.value[selectedToothArea.value]
  } else {
    st = toothStatuses.value['tooth_circle']
  }
  st = Object.values(st)
  st.sort((a, b) => (a.clinic_id > b.clinic_id) ? 1 : (a.clinic_id === b.clinic_id) ? ((a.title > b.title) ? 1 : -1) : -1 )
  st.unshift({
    id: 0,
    title: i18n.t('norm'),
    color: '#ffffff'
  })
  return st
})

const toothStatusesOfSelectedAreas = computed(() => {
  const arr = []
  const statuses = teeth.value.find(x => x.tooth_number == selectedTooth.value)
  for(const area in statuses) {
    if(area === selectedToothArea.value || selectedToothAreas.value.includes(area)) {
      arr.push(statuses[area])
    }
  }
  return arr
})

const innerTooth = computed(() => {
  return [
    'tooth_inner_right',
    'tooth_inner_left',
    'tooth_left',
    'tooth_top',
    'tooth_right',
    'tooth_bottom',
    'tooth_inner'
  ].includes(selectedToothArea.value)
})

const canEdit = computed(() => rights.value.includes('tooth_statuses'))

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  noty = instance?.appContext?.app?.noty
  bvModal = instance?.ctx?._bv__modal
  i18n = instance?.ctx?.$i18n
})

const statusSelected = async (status) => {
  try {
    const res = await PatientService.updateClientTeeth(patient.value.id, selectedTooth.value, {
      status_id: status.id,
      area: selectedToothArea.value,
      areas: selectedToothAreas.value,
      all: whole.value
    })
    const d = res.data
    store.commit('setTeeth', d.teeth)
    store.commit('setStatuses', d.statuses)
    bvModal.hide('tooth-status-modal')
  } catch (e) {
    console.log(e)
  }
}

const onHide = () => {
  store.commit('cleanValidationErrors')
  store.commit("setSelectedTooth", {
    tooth: null,
    areas: [],
    area: null
  })
  editingStatus.value = null
}

const saveStatus = async () => {
  let formData = new FormData()
  formData = appendFormdata(formData, editingStatus.value)
  formData.append('area', selectedToothArea.value)
  loading.value = true
  try {
    if(editingStatus.value.id) {
      await EloquentService.update('tooth_status', editingStatus.value.id, formData)
    } else {
      await EloquentService.create('tooth_status', formData)
    }
    editingStatus.value = null
    await store.dispatch('getPatientTeeth', patient.value.id)
  } finally {
    loading.value = false
  }
  await loadReportUses()
}

const addNew = () => {
  editingStatus.value = {
    title: '',
    color: '#ffffff',
    clinic_id: clinic.value.id,
    short_name: '',
    useInForm: false
  }
}

const editStatus = (s) => {
  if(editingStatus.value?.id === s.id) {
    editingStatus.value = null
    return
  }
  editingStatus.value = s
  editingStatus.value.useInForm = statusesInReports.value.includes(s.id) || s.standardf43
  counter.value++
  scrollToBottom()
}

const scrollToBottom = () => {
  const el = document.getElementById('tooth-status-modal')
  if(!el) return
  el.style.scrollBehavior = 'smooth'
  el.scrollTo(0, el.scrollHeight + 5000, { behavior: 'smooth' })
}

const deleteStatus = async (s) => {
  let res = confirm(i18n.t('are_you_sure_you_want_to_delete_status'))
  if(!res) return
  try {
    await EloquentService.delete('tooth_status', s.id)
    noty.info(i18n.t('success_message'))
    let newTS = copyObject(toothStatuses.value)
    for(const area in newTS) {
      for(const id in newTS[area]) {
        if(id == s.id) {
          delete newTS[area][s.id];
        }
      }
    }
    store.commit('setStatuses', newTS)
  } catch (e) {
    console.log(e)
  }
}

const loadReportUses = async () => {
  try {
    const res = await ClinicService.teethStatusesForReport()
    statusesInReports.value = res.data
  } catch (e) {
    statusesInReports.value = []
  }
  counter.value++
}

const cancel = () => {
  editingStatus.value = null
}

watch(innerTooth, (val) => {
  if(!val) {
    whole.value = false
  }
})
</script>

<style scoped lang="scss">
#tooth-status-modal {
  font-size: 14px;
}
.tooth-status-color {
  border-radius: 100%;
  width: 24px;
  height: 24px;
  display: inline-block;
  &.bordered {
    border: 2px solid #eee;
  }
}
.edit-svg-btn.standard {
  opacity: 0.15;
}
.status-item {
  &.active, &:hover {
    .title {
      color: $blue-color;
    }
  }
  &:hover {
    .edit-svg-btn.standard {
      opacity: 1;
    }
  }
}
</style>
